import { useEffect, useMemo, useRef, useState } from 'react';
import {
  BarChart3,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Eye,
  Maximize2,
  Minimize2,
  PackageCheck,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import { jsPDF } from 'jspdf';
import AppShell from '../app/AppShell';
import {
  CustomDatePicker,
  CustomMonthPicker,
  CustomSelect,
  FeedbackMessage,
  ModalBackdrop,
  PaginationFooter,
  ToastStack,
} from '../components/common';
import useReleasings from '../hooks/useReleasings';
import { formatPeso, formatShortDate } from '../utils/formatters';

function SalesAnalyticsPage({ navigationItems, activePath, onNavigate }) {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isTrendExpanded, setIsTrendExpanded] = useState(false);
  const [isRecentExpanded, setIsRecentExpanded] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const { releaseHistory, isLoading, loadError } = useReleasings();
  const salesReport = useMemo(
    () => buildSalesReport(releaseHistory, selectedYear),
    [releaseHistory, selectedYear]
  );
  const toasts = loadError
    ? [{ id: 'sales-analytics-load-error', tone: 'error', message: loadError }]
    : [];

  useEffect(() => {
    setSelectedRelease(null);
  }, [selectedYear]);

  return (
    <AppShell
      navigationItems={navigationItems}
      activePath={activePath}
      onNavigate={onNavigate}
    >
      <ToastStack toasts={toasts} />
      <section className="mt-5 px-4 sm:mt-6 sm:px-6 lg:px-[26px]">
        <div className="grid gap-5">
          <div className="rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.05)]">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px] lg:items-start">
              <div className="min-w-0">
                <h2 className="m-0 text-[1.25rem] font-semibold text-[var(--theme-text)] sm:text-[1.4rem]">
                  Release Analytics
                </h2>
                <p className="m-0 mt-2 max-w-[760px] text-[0.95rem] leading-6 text-[var(--theme-text-muted)]">
                  Review released volume, customers, batches, and release costs by month.
                </p>
              </div>
              <YearPicker
                label="Release Year"
                value={selectedYear}
                onChange={setSelectedYear}
                currentYear={currentYear}
              />
            </div>
          </div>

          {isLoading ? (
            <FeedbackMessage tone="info" message="Loading release analytics..." />
          ) : (
            <>
              <SalesSummary summary={salesReport.summary} year={selectedYear} />
              <MonthlySalesPanel items={salesReport.monthlySales} year={selectedYear} />
              <div className="grid min-w-0 gap-5 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                <SalesTrendGraph
                  items={salesReport.monthlySales}
                  year={selectedYear}
                  isExpanded={isTrendExpanded}
                  onToggleExpand={() => setIsTrendExpanded((currentValue) => !currentValue)}
                />
                <RecentSalesPanel
                  items={salesReport.recentSales}
                  year={selectedYear}
                  isExpanded={isRecentExpanded}
                  onToggleExpand={() => setIsRecentExpanded((currentValue) => !currentValue)}
                  onOpenRelease={setSelectedRelease}
                />
              </div>
              {selectedRelease ? (
                <RecentSalesReleaseDetailModal
                  release={selectedRelease}
                  onClose={() => setSelectedRelease(null)}
                />
              ) : null}
            </>
          )}
        </div>
      </section>
    </AppShell>
  );
}

function YearPicker({ label, value, onChange, currentYear }) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewYear, setViewYear] = useState(Number(value) || currentYear);
  const pickerRef = useRef(null);
  const decadeStart = Math.floor(viewYear / 10) * 10;
  const years = Array.from({ length: 12 }, (_, index) => decadeStart - 1 + index);

  useEffect(() => {
    setViewYear(Number(value) || currentYear);
  }, [currentYear, value]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!pickerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  function handleSelect(year) {
    onChange(year);
    setIsOpen(false);
  }

  return (
    <label className="relative grid min-w-0 gap-1.5" ref={pickerRef}>
      <span className="text-[0.74rem] font-medium text-[var(--theme-text-subtle)]">
        {label}
      </span>
      <button
        className="flex min-h-[46px] w-full items-center justify-between gap-2 rounded-full border border-[var(--theme-border)] bg-white px-4 py-2 text-left text-[0.9rem] font-semibold text-[var(--theme-text)] outline-none transition hover:bg-[var(--theme-surface-soft)] focus:border-[var(--theme-accent)] focus:ring-2 focus:ring-[var(--theme-accent-soft)]"
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
        onClick={() => setIsOpen((currentValue) => !currentValue)}
      >
        <span>{value}</span>
        <CalendarDays className="h-4 w-4 text-[var(--theme-accent)]" aria-hidden="true" />
      </button>

      {isOpen ? (
        <div className="absolute right-0 top-full z-30 mt-2 w-[min(300px,calc(100vw-2rem))] rounded-[22px] border border-[var(--theme-border)] bg-white p-3 shadow-[0_18px_45px_rgba(var(--theme-shadow-rgb),0.16)]">
          <div className="relative flex min-h-[38px] items-center justify-center px-11">
            <button
              className="absolute left-0 grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              aria-label="Previous decade"
              onClick={() => setViewYear((year) => year - 10)}
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <span className="text-[0.96rem] font-semibold text-[var(--theme-text-strong)]">
              {decadeStart} - {decadeStart + 9}
            </span>
            <button
              className="absolute right-0 grid h-9 w-9 place-items-center rounded-xl border border-[var(--theme-border)] bg-white text-[var(--theme-accent)] transition hover:bg-[var(--theme-surface-soft)]"
              type="button"
              aria-label="Next decade"
              onClick={() => setViewYear((year) => year + 10)}
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          <div className="mt-3 grid grid-cols-3 gap-1.5">
            {years.map((year) => {
              const isSelected = year === Number(value);
              const isCurrentYear = year === currentYear;

              return (
                <button
                  key={year}
                  className={`grid min-h-[40px] place-items-center rounded-xl text-[0.88rem] transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] ${
                    isSelected
                      ? 'bg-[var(--theme-accent)] font-semibold text-white hover:bg-[var(--theme-accent-strong)]'
                      : isCurrentYear
                        ? 'font-semibold text-[var(--theme-accent)] ring-1 ring-inset ring-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)]'
                        : 'text-[var(--theme-text-strong)] hover:bg-[var(--theme-accent-soft)]'
                  }`}
                  type="button"
                  onClick={() => handleSelect(year)}
                >
                  {year}
                </button>
              );
            })}
          </div>

          <div className="mt-3 flex justify-end border-t border-[var(--theme-border)] pt-2">
            <button
              className="rounded-full border border-[var(--theme-border)] bg-[var(--theme-surface-warm)] px-3 py-1.5 text-[0.78rem] font-semibold text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-accent-soft)] hover:text-[var(--theme-text-strong)]"
              type="button"
              onClick={() => handleSelect(currentYear)}
            >
              Current Year
            </button>
          </div>
        </div>
      ) : null}
    </label>
  );
}

function SalesSummary({ summary, year }) {
  const cards = [
    {
      label: 'Released KG',
      value: formatKg(summary.releasedKg),
      detail: `${summary.releaseCount} release${summary.releaseCount === 1 ? '' : 's'}`,
      icon: TrendingUp,
    },
    {
      label: 'Release Cost',
      value: formatPeso(summary.totalCost),
      detail: 'inventory cost + release expense',
      icon: BarChart3,
    },
    {
      label: 'Released Batches',
      value: String(summary.releaseCount),
      detail: `${summary.customerCount} customer${summary.customerCount === 1 ? '' : 's'}`,
      icon: PackageCheck,
    },
    {
      label: 'Average KG/Release',
      value: formatKg(summary.averageKgPerRelease),
      detail: `${year} released volume`,
      icon: CalendarDays,
    },
  ];

  return (
    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;

        return (
          <article
            key={card.label}
            className="min-w-0 rounded-[16px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_24px_rgba(var(--theme-shadow-rgb),0.04)]"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
                  {card.label}
                </div>
                <div className="mt-2 truncate text-[1.22rem] font-semibold text-[var(--theme-text)]">
                  {card.value}
                </div>
                <div className="mt-1 text-[0.82rem] text-[var(--theme-text-muted)]">
                  {card.detail}
                </div>
              </div>
              <div className="grid h-10 w-10 shrink-0 place-items-center rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] text-[var(--theme-accent-strong)]">
                <Icon size={18} aria-hidden="true" />
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
}

function SalesTrendGraph({ items, year, isExpanded, onToggleExpand }) {
  const panelRef = useRef(null);
  const [hoveredTrendIndex, setHoveredTrendIndex] = useState(null);
  const chartItems = [...items].reverse();
  const releasedKgColor = 'var(--theme-accent)';
  const chartWidth = 1080;
  const chartHeight = isExpanded ? 460 : 360;
  const margin = {
    top: 28,
    right: 28,
    bottom: 58,
    left: 86,
  };
  const innerWidth = chartWidth - margin.left - margin.right;
  const innerHeight = chartHeight - margin.top - margin.bottom;
  const maxValue = Math.max(...chartItems.map((item) => item.releasedKg), 1);
  const yTicks = buildYAxisTicks(maxValue);
  const points = chartItems.map((item, index) =>
    toChartPoint(index, item.releasedKg, chartItems.length, innerWidth, innerHeight, maxValue)
  );

  useExpandedPanelFocus(panelRef, isExpanded);

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`sales-analytics-expand-panel rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)] ${
        isExpanded ? 'sales-analytics-expand-panel--expanded xl:col-span-2' : ''
      }`}
    >
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <PanelHeader
            title="Release Trend"
            description={`Monthly released KG for ${year}.`}
          />
          <div className="-mt-2 flex flex-wrap gap-3 text-[0.8rem] text-[var(--theme-text-muted)]">
          <LegendDot color={releasedKgColor} label="Released KG" />
          </div>
        </div>
        <div className="shrink-0">
          <ExpandPanelButton
            isExpanded={isExpanded}
            onClick={onToggleExpand}
            label={isExpanded ? 'Shrink sales trend' : 'Expand sales trend'}
          />
        </div>
      </div>

      {chartItems.length ? (
        <div className="min-w-0 overflow-hidden">
          <svg
            className="block h-auto w-full max-w-full rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)]"
            viewBox={`0 0 ${chartWidth} ${chartHeight}`}
            role="img"
            aria-label="Monthly released KG and release cost trend"
          >
            <g transform={`translate(${margin.left}, ${margin.top})`}>
              {yTicks.map((tick) => {
                const y = getChartY(tick, innerHeight, maxValue);

                return (
                  <g key={tick}>
                    <line
                      x1="0"
                      x2={innerWidth}
                      y1={y}
                      y2={y}
                      stroke="#d7e2df"
                      strokeWidth="1"
                    />
                    <text
                      x="-14"
                      y={y + 4}
                      textAnchor="end"
                      fontSize="12"
                      fill="#5c7070"
                    >
                      {formatCompactNumber(tick)}
                    </text>
                  </g>
                );
              })}

              <path
                d={buildSmoothPath(points)}
                fill="none"
                stroke={releasedKgColor}
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map((point, index) => (
                <g key={chartItems[index].key}>
                  <line
                    x1={point.x}
                    x2={point.x}
                    y1={point.y}
                    y2={innerHeight}
                    stroke="#c9d8d4"
                    strokeWidth="1"
                    strokeDasharray="4 7"
                  />
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="10"
                    fill="transparent"
                    onMouseEnter={() => setHoveredTrendIndex(index)}
                    onMouseLeave={() =>
                      setHoveredTrendIndex((currentIndex) => (currentIndex === index ? null : currentIndex))
                    }
                  />
                  <circle cx={point.x} cy={point.y} r="6" fill={releasedKgColor} />
                  <text
                    x={point.x}
                    y={innerHeight + 28}
                    textAnchor="middle"
                    fontSize="12"
                    fill="#405757"
                  >
                    {formatMonthLabel(chartItems[index].label)}
                  </text>
                </g>
              ))}
              {hoveredTrendIndex !== null && chartItems[hoveredTrendIndex] ? (
                <TrendTooltip
                  item={chartItems[hoveredTrendIndex]}
                  point={points[hoveredTrendIndex]}
                  innerWidth={innerWidth}
                />
              ) : null}
            </g>
          </svg>
        </div>
      ) : (
        <EmptyReport message={`No release trend found for ${year}.`} />
      )}
    </section>
  );
}

function MonthlySalesPanel({ items, year }) {
  const panelRef = useRef(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const getBarValue = (item) => Number(item.releasedKg || 0);
  const maxBarValue = Math.max(...items.map(getBarValue), 1);
  const collapsedItems = getCollapsedMonthlyItems(items, year);
  const visibleItems = isExpanded ? items : collapsedItems;

  useEffect(() => {
    setIsExpanded(false);
  }, [year]);

  useExpandedPanelFocus(panelRef, isExpanded);

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`sales-analytics-expand-panel rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)] ${
        isExpanded ? 'sales-analytics-expand-panel--expanded' : ''
      }`}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PanelHeader
          title="Monthly Release Report"
          description={`Released volume and cost grouped by month for ${year}.`}
        />
        {items.length > collapsedItems.length ? (
          <button
            className="inline-flex min-h-[40px] w-max items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.82rem] font-semibold text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text)]"
            type="button"
            onClick={() => setIsExpanded((currentValue) => !currentValue)}
          >
            {isExpanded ? 'Show Less' : 'Show All Months'}
            <ChevronDown
              className={`h-4 w-4 transition ${isExpanded ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        ) : null}
      </div>
      {visibleItems.length ? (
        <div className="grid gap-3">
          {visibleItems.map((item) => (
            <div
              key={item.key}
              className="grid gap-3 rounded-[14px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-3 lg:grid-cols-[170px_minmax(0,1fr)_160px_160px]"
            >
              <div>
                <div className="font-semibold text-[var(--theme-text)]">{item.label}</div>
                <div className="mt-0.5 text-[0.78rem] text-[var(--theme-text-muted)]">
                  {item.releaseCount} release{item.releaseCount === 1 ? '' : 's'}
                </div>
              </div>
              <div className="min-w-0 self-center">
                <div className="h-2.5 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-[var(--theme-accent)]"
                    style={{
                      width: getBarValue(item)
                        ? `${Math.max((getBarValue(item) / maxBarValue) * 100, 2)}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
              <MetricText label="Cost" value={formatPeso(item.totalCost)} />
              <MetricText label="KG" value={formatKg(item.releasedKg)} />
            </div>
          ))}
        </div>
      ) : (
        <EmptyReport message={`No releases found for ${year}.`} />
      )}
    </section>
  );
}

function RecentSalesPanel({ items, year, isExpanded, onToggleExpand, onOpenRelease }) {
  const panelRef = useRef(null);
  const isWideLayout = useIsWideAnalyticsLayout();
  const pageSize = isWideLayout && !isExpanded ? 5 : 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [filterMode, setFilterMode] = useState('month');
  const [filterMonth, setFilterMonth] = useState(() => getDefaultMonthFilter(year));
  const [filterDate, setFilterDate] = useState(() => getDefaultDateFilter(year));
  const [filterFromDate, setFilterFromDate] = useState(
    () => getMonthDateRange(getDefaultMonthFilter(year)).from
  );
  const [filterToDate, setFilterToDate] = useState(
    () => getMonthDateRange(getDefaultMonthFilter(year)).to
  );
  const filteredItems = useMemo(
    () =>
      filterRecentSales(
        items,
        {
          mode: filterMode,
          month: filterMonth,
          date: filterDate,
          fromDate: filterFromDate,
          toDate: filterToDate,
        },
        year
      ),
    [filterDate, filterFromDate, filterMode, filterMonth, filterToDate, items, year]
  );
  const totalItems = filteredItems.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const normalizedPage = Math.min(currentPage, totalPages);
  const startIndex = (normalizedPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  const paginatedItems = filteredItems.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredItems, pageSize, year]);

  useEffect(() => {
    const defaultMonth = getDefaultMonthFilter(year);
    const defaultRange = getMonthDateRange(defaultMonth);

    setFilterMode('month');
    setFilterMonth(defaultMonth);
    setFilterDate(getDefaultDateFilter(year));
    setFilterFromDate(defaultRange.from);
    setFilterToDate(defaultRange.to);
  }, [year]);

  useExpandedPanelFocus(panelRef, isExpanded);

  return (
    <section
      ref={panelRef}
      tabIndex={-1}
      className={`sales-analytics-expand-panel rounded-[18px] border border-[var(--theme-border-soft)] bg-white p-4 shadow-[0_10px_28px_rgba(var(--theme-shadow-rgb),0.04)] ${
        isExpanded ? 'sales-analytics-expand-panel--expanded xl:col-span-2' : ''
      }`}
    >
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <PanelHeader
          title="Recent Releases"
          description={`Latest released batches used for ${year} release reporting.`}
        />
        <ExpandPanelButton
          isExpanded={isExpanded}
          onClick={onToggleExpand}
          label={isExpanded ? 'Shrink recent released sales' : 'Expand recent released sales'}
        />
      </div>
      <RecentSalesFilterBar
        filterMode={filterMode}
        filterMonth={filterMonth}
        filterDate={filterDate}
        filterFromDate={filterFromDate}
        filterToDate={filterToDate}
        hasItems={filteredItems.length > 0}
        selectedYear={year}
        onDownload={() =>
          downloadRecentSalesPdf(filteredItems, year, {
            mode: filterMode,
            month: filterMonth,
            date: filterDate,
            fromDate: filterFromDate,
            toDate: filterToDate,
          })
        }
        onFilterModeChange={setFilterMode}
        onFilterMonthChange={setFilterMonth}
        onFilterDateChange={setFilterDate}
        onFilterFromDateChange={setFilterFromDate}
        onFilterToDateChange={setFilterToDate}
      />
      {items.length ? (
        <div className="grid gap-3">
          <div className="grid gap-2 md:hidden">
            {paginatedItems.map((item) => (
              <RecentSaleCard
                key={item.id}
                item={item}
                onOpenRelease={() => onOpenRelease(item)}
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto md:block">
            <table className="min-w-[780px] w-full border-collapse text-left text-[0.88rem] transition-all duration-300 ease-out">
              <thead className="bg-[var(--theme-accent-panel)] text-[0.72rem] uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
                <tr>
                  <th className="px-4 py-3">Release No.</th>
                  <th className="px-4 py-3">Batch</th>
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">KG</th>
                  <th className="px-4 py-3">Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--theme-border-soft)]">
                {paginatedItems.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-3 py-1.5 font-semibold text-[var(--theme-accent-strong)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)]"
                        onClick={() => onOpenRelease(item)}
                      >
                        <span>{item.releaseCode}</span>
                        <Eye size={14} strokeWidth={2.1} aria-hidden="true" />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <BatchFclCell item={item} />
                    </td>
                    <td className="px-4 py-3">{item.customerName}</td>
                    <td className="px-4 py-3">{formatShortDate(item.releaseDate)}</td>
                    <td className="px-4 py-3">{formatKg(item.releasedKg)}</td>
                    <td className="px-4 py-3">{formatPeso(item.totalCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalItems ? (
            <div className="overflow-hidden rounded-[16px] border border-[var(--theme-border-soft)]">
              <PaginationFooter
                currentPage={normalizedPage}
                endItem={endIndex}
                isNextDisabled={normalizedPage >= totalPages}
                isPreviousDisabled={normalizedPage <= 1}
                onNext={() => setCurrentPage((page) => Math.min(page + 1, totalPages))}
                onPrevious={() => setCurrentPage((page) => Math.max(page - 1, 1))}
                startItem={totalItems ? startIndex + 1 : 0}
                totalItems={totalItems}
                totalPages={totalPages}
              />
            </div>
          ) : null}
        </div>
      ) : (
        <EmptyReport
          message={
            items.length
              ? 'No recent released sales match the selected filters.'
              : `No recent released sales found for ${year}.`
          }
        />
      )}
    </section>
  );
}

function useIsWideAnalyticsLayout() {
  const [isWideLayout, setIsWideLayout] = useState(() =>
    typeof window === 'undefined' ? true : window.matchMedia('(min-width: 1280px)').matches
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(min-width: 1280px)');
    const updateLayout = () => setIsWideLayout(mediaQuery.matches);

    updateLayout();
    mediaQuery.addEventListener('change', updateLayout);

    return () => {
      mediaQuery.removeEventListener('change', updateLayout);
    };
  }, []);

  return isWideLayout;
}

function RecentSaleCard({ item, onOpenRelease }) {
  return (
    <article className="rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
            Release No.
          </div>
          <button
            type="button"
            className="mt-1 inline-flex max-w-full items-center gap-2 rounded-full border border-[var(--theme-border-soft)] bg-white px-2.5 py-1 font-semibold text-[var(--theme-accent-strong)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-accent-soft)]"
            onClick={onOpenRelease}
          >
            <span className="truncate">{item.releaseCode}</span>
            <Eye size={14} strokeWidth={2.1} aria-hidden="true" />
          </button>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
            Date
          </div>
          <div className="mt-1 font-medium text-[var(--theme-text)]">
            {formatShortDate(item.releaseDate)}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <BatchFclCell item={item} />
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 text-[0.82rem]">
        <RecentSaleMetric label="Customer" value={item.customerName} />
        <RecentSaleMetric label="KG" value={formatKg(item.releasedKg)} />
        <RecentSaleMetric label="Cost" value={formatPeso(item.totalCost)} />
      </div>
    </article>
  );
}

function RecentSalesFilterBar({
  filterMode,
  filterMonth,
  filterDate,
  filterFromDate,
  filterToDate,
  hasItems,
  selectedYear,
  onDownload,
  onFilterModeChange,
  onFilterMonthChange,
  onFilterDateChange,
  onFilterFromDateChange,
  onFilterToDateChange,
}) {
  const modeOptions = [
    { value: 'month', label: 'By Month' },
    { value: 'date', label: 'By Date' },
    { value: 'custom', label: 'Custom Date' },
  ];

  function handleFilterModeChange(event) {
    const nextMode = String(event.target.value || 'month');

    onFilterModeChange(nextMode);

    if (nextMode === 'month' && !filterMonth) {
      onFilterMonthChange(getDefaultMonthFilter(selectedYear));
    }

    if (nextMode === 'date' && !filterDate) {
      onFilterDateChange(getDefaultDateFilter(selectedYear));
    }

    if (nextMode === 'custom' && (!filterFromDate || !filterToDate)) {
      const range = getMonthDateRange(filterMonth || getDefaultMonthFilter(selectedYear));

      onFilterFromDateChange(filterFromDate || range.from);
      onFilterToDateChange(filterToDate || range.to);
    }
  }

  return (
    <div className="mb-4 rounded-[16px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] p-3">
      <div className="grid gap-3 md:grid-cols-[180px_minmax(0,210px)_minmax(0,210px)_minmax(0,210px)_1fr_auto] md:items-end">
        <CustomSelect
          label="Filter"
          name="recentSalesFilterMode"
          value={filterMode}
          onChange={handleFilterModeChange}
          options={modeOptions}
          searchable={false}
          clearOptionLabel=""
          triggerShapeClassName="rounded-[12px]"
          wrapperClassName="relative grid w-full min-w-0 self-start gap-1.5"
        />
        {filterMode === 'month' ? (
          <CustomMonthPicker
            label="Month"
            name="recentSalesFilterMonth"
            value={filterMonth}
            onChange={(event) => onFilterMonthChange(normalizeMonthFilter(event.target.value, selectedYear))}
            placeholder={`Select month in ${selectedYear}`}
            triggerShapeClassName="rounded-[12px]"
            wrapperClassName="relative grid w-full min-w-0 self-start gap-1.5"
          />
        ) : null}
        {filterMode === 'date' ? (
          <CustomDatePicker
            label="Date"
            name="recentSalesFilterDate"
            value={filterDate}
            onChange={(event) => onFilterDateChange(normalizeDateFilter(event.target.value, selectedYear))}
            placeholder="MM-DD-YYYY"
            triggerShapeClassName="rounded-[12px]"
            wrapperClassName="relative grid w-full min-w-0 self-start gap-1.5"
          />
        ) : null}
        {filterMode === 'custom' ? (
          <>
            <CustomDatePicker
              label="From"
              name="recentSalesFilterFrom"
              value={filterFromDate}
              onChange={(event) =>
                onFilterFromDateChange(normalizeDateFilter(event.target.value, selectedYear))
              }
              placeholder="MM-DD-YYYY"
              triggerShapeClassName="rounded-[12px]"
              wrapperClassName="relative grid w-full min-w-0 self-start gap-1.5"
            />
            <CustomDatePicker
              label="To"
              name="recentSalesFilterTo"
              value={filterToDate}
              onChange={(event) =>
                onFilterToDateChange(normalizeDateFilter(event.target.value, selectedYear))
              }
              placeholder="MM-DD-YYYY"
              triggerShapeClassName="rounded-[12px]"
              wrapperClassName="relative grid w-full min-w-0 self-start gap-1.5"
            />
          </>
        ) : null}
        <div className="md:col-start-6 md:justify-self-end">
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[var(--theme-border)] bg-white text-[var(--theme-text-subtle)] transition hover:border-[var(--theme-accent)] hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-accent-strong)] disabled:cursor-not-allowed disabled:opacity-45"
            onClick={onDownload}
            disabled={!hasItems}
            title="Download PDF"
            aria-label="Download PDF"
          >
            <Download size={17} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
}

function RecentSalesReleaseDetailModal({ release, onClose }) {
  const items = Array.isArray(release.items) ? release.items : [];
  const expensePerSack = Number(release.expensePerSack || 0);
  const totalFclExpense = Number(release.releasedSacks || 0) * expensePerSack;

  return (
    <ModalBackdrop>
      <div className="flex max-h-[calc(100vh-3rem)] w-full max-w-[980px] flex-col overflow-hidden rounded-[18px] border border-[var(--theme-border-soft)] bg-white shadow-[0_24px_70px_rgba(64,52,42,0.24)]">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--theme-border-soft)] px-5 py-4">
          <div className="min-w-0">
            <h2 className="m-0 text-[1.2rem] font-semibold text-[var(--theme-text)]">
              {release.releaseCode}
            </h2>
            <div className="mt-1 text-[0.86rem] text-[var(--theme-text-muted)]">
              {release.soCode || '-'} · {release.customerName || '-'} · {formatShortDate(release.releaseDate)}
            </div>
          </div>
          <button
            type="button"
            className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-[var(--theme-border)] text-[var(--theme-text-subtle)] transition hover:bg-[var(--theme-surface-soft)]"
            onClick={onClose}
            aria-label="Close release details"
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <AnalyticsSummaryTile label="Slot Code" value={release.slotCode || '-'} />
            <AnalyticsSummaryTile label="Released Sacks" value={formatSacksQuantity(release.releasedSacks || 0)} />
            <AnalyticsSummaryTile label="Released KG" value={formatKg(release.releasedKg || 0)} strong />
            <AnalyticsSummaryTile label="Status" value={release.status || '-'} />
          </div>

          <div className="mt-4 overflow-hidden rounded-[14px] border border-[var(--theme-border-soft)]">
            <div className="bg-[var(--theme-accent-panel)] px-4 py-3 text-[0.74rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-accent-panel-text)]">
              Released P.O.s
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[900px] w-full border-collapse text-left text-[0.84rem]">
                <thead className="bg-[var(--theme-surface-soft)] text-[0.68rem] uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
                  <tr>
                    <th className="px-4 py-2.5">P.O.</th>
                    <th className="px-4 py-2.5">Sacks Mark</th>
                    <th className="px-4 py-2.5">Warehouse</th>
                    <th className="px-4 py-2.5">Sacks</th>
                    <th className="px-4 py-2.5">Net KG</th>
                    <th className="px-4 py-2.5">Unit Cost</th>
                    <th className="px-4 py-2.5">FCL Expense</th>
                    <th className="px-4 py-2.5">Total Capital</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--theme-border-soft)]">
                  {items.length ? (
                    items.map((item) => {
                      const totalCapital = Number(item.subtotal || 0) + totalFclExpense;

                      return (
                        <tr key={item.id || item.sourceKey}>
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[var(--theme-text)]">{item.poCode || '-'}</div>
                            <div className="text-[0.72rem] text-[var(--theme-text-muted)]">
                              {getReleaseItemStatusLabel(item)}
                            </div>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-text)]">
                            {getInventorySacksMark(item)}
                          </td>
                          <td className="px-4 py-3">{item.warehouseLocation || '-'}</td>
                          <td className="px-4 py-3 font-semibold">{formatSacksQuantity(item.releaseQuantity || 0)}</td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-accent-strong)]">{formatKg(item.quantityKg || 0)}</td>
                          <td className="px-4 py-3">{formatPesoWithDecimals(item.unitCost || 0)}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex min-h-[46px] flex-col justify-center rounded-[12px] border border-[#d6e3df] bg-[#f5fbf9] px-3 py-1 text-[#496862] shadow-[0_6px_14px_rgba(73,104,98,0.08)]">
                              <span className="text-[0.78rem] font-bold leading-tight">
                                {formatPeso(expensePerSack)} / sack
                              </span>
                              <span className="text-[0.7rem] font-semibold leading-tight">
                                {formatPeso(totalFclExpense)} Tot. Exp.
                              </span>
                            </span>
                          </td>
                          <td className="px-4 py-3 font-semibold text-[var(--theme-text)]">
                            {formatPeso(totalCapital)}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td className="px-4 py-7 text-center text-[var(--theme-text-muted)]" colSpan={8}>
                        No P.O. items recorded for this release.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {release.remarks ? (
            <div className="mt-4 rounded-[12px] border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-4 py-3 text-[0.86rem] text-[var(--theme-text)]">
              <div className="mb-1 text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
                Remarks
              </div>
              {release.remarks}
            </div>
          ) : null}
        </div>
      </div>
    </ModalBackdrop>
  );
}

function AnalyticsSummaryTile({ label, value, strong = false, highlight = false }) {
  return (
    <div
      className={`flex min-h-[72px] min-w-0 flex-col justify-center rounded-[12px] border px-3 py-2.5 ${
        highlight
          ? 'border-[#80b985] bg-[#eaf8ec] shadow-[0_10px_20px_rgba(60,122,65,0.12)] ring-1 ring-[#bfe5c2]'
          : 'border-[var(--theme-border-soft)] bg-white'
      }`}
    >
      <div className={`truncate text-[0.68rem] font-semibold uppercase tracking-[0.08em] ${highlight ? 'text-[#2f6f3a]' : 'text-[var(--theme-text-subtle)]'}`}>{label}</div>
      <div className={`mt-1 truncate text-[0.95rem] leading-tight ${highlight ? 'font-bold text-[#256c32]' : strong ? 'font-bold text-[var(--theme-accent-strong)]' : 'font-semibold text-[var(--theme-text)]'}`}>{value}</div>
    </div>
  );
}

function TrendTooltip({ item, point, innerWidth }) {
  const tooltipWidth = 196;
  const tooltipHeight = 78;
  const tooltipX = Math.max(0, Math.min(point.x - tooltipWidth / 2, innerWidth - tooltipWidth));
  const tooltipY = Math.max(0, point.y - tooltipHeight - 18);

  return (
    <g pointerEvents="none" transform={`translate(${tooltipX}, ${tooltipY})`}>
      <rect width={tooltipWidth} height={tooltipHeight} rx="14" fill="#ffffff" stroke="#cddbd7" strokeWidth="1" />
      <text x="14" y="20" fontSize="12" fontWeight="700" fill="#3f5b59">
        {item.label}
      </text>
      <text x="14" y="39" fontSize="11" fill="#355e57">
        Released KG: {formatKg(item.releasedKg)}
      </text>
      <text x="14" y="55" fontSize="11" fill="#426979">
        Cost: {formatPeso(item.totalCost)}
      </text>
      <text x="14" y="71" fontSize="11" fill="#5d7876">
        KG: {formatKg(item.releasedKg)} | Releases: {item.releaseCount}
      </text>
    </g>
  );
}

function RecentSaleMetric({ label, value }) {
  return (
    <div className="min-w-0 rounded-[12px] bg-white px-2.5 py-2">
      <div className="truncate text-[0.68rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
        {label}
      </div>
      <div className="mt-1 truncate font-semibold text-[var(--theme-text)]">
        {value || '-'}
      </div>
    </div>
  );
}

function useExpandedPanelFocus(panelRef, isExpanded) {
  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      const panelElement = panelRef.current;

      if (!panelElement) {
        return;
      }

      panelElement.focus({ preventScroll: true });
      panelElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    }, 120);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isExpanded, panelRef]);
}

function ExpandPanelButton({ isExpanded, onClick, label }) {
  const Icon = isExpanded ? Minimize2 : Maximize2;

  return (
    <button
      className="hidden min-h-[38px] items-center gap-2 rounded-full border border-[var(--theme-border)] bg-white px-3 py-2 text-[0.78rem] font-semibold text-[var(--theme-text-muted)] transition hover:bg-[var(--theme-surface-soft)] hover:text-[var(--theme-text)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-focus)] xl:inline-flex"
      type="button"
      aria-label={label}
      title={label}
      onClick={onClick}
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      <span className="hidden sm:inline">{isExpanded ? 'Shrink' : 'Expand'}</span>
    </button>
  );
}

function BatchFclCell({ item }) {
  const batchLabel = item.soCode || '-';
  const fclLabel = formatFclReference(item);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="font-semibold text-[var(--theme-text)]">{batchLabel}</span>
      {fclLabel ? (
        <>
          <span className="h-4 w-px bg-[var(--theme-border)]" aria-hidden="true" />
          <span className="rounded-full border border-[var(--theme-border-soft)] bg-[var(--theme-surface-soft)] px-2.5 py-1 text-[0.74rem] font-semibold text-[var(--theme-text-muted)]">
            {fclLabel}
          </span>
        </>
      ) : null}
    </div>
  );
}

function formatFclReference(item) {
  const fclSlotNumber = Number(item.fclSlotNumber || 0);
  const slotCode = String(item.slotCode || '').trim();
  const fclBatchNumber = String(item.fclBatchNumber || '').trim();

  if (Number.isFinite(fclSlotNumber) && fclSlotNumber > 0) {
    return `FCL ${fclSlotNumber}`;
  }

  if (slotCode) {
    const slotMatch = slotCode.match(/(\d+)$/);

    return slotMatch ? `FCL ${Number(slotMatch[1])}` : `FCL ${slotCode}`;
  }

  if (fclBatchNumber) {
    return `FCL ${fclBatchNumber}`;
  }

  return '';
}

function PanelHeader({ title, description }) {
  return (
    <div className="mb-4">
      <h3 className="m-0 text-[1.05rem] font-semibold text-[var(--theme-text)]">
        {title}
      </h3>
      <p className="m-0 mt-1 text-[0.9rem] leading-6 text-[var(--theme-text-muted)]">
        {description}
      </p>
    </div>
  );
}

function MetricText({ label, value }) {
  return (
    <div className="lg:text-right">
      <div className="text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-[var(--theme-text-subtle)]">
        {label}
      </div>
      <div className="mt-1 font-semibold text-[var(--theme-text)]">{value}</div>
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <div className="inline-flex items-center gap-2">
      <span className="h-3 w-3 rounded-full" style={{ backgroundColor: color }} />
      <span>{label}</span>
    </div>
  );
}

function EmptyReport({ message }) {
  return (
    <div className="rounded-[14px] border border-dashed border-[var(--theme-border)] bg-[var(--theme-surface-soft)] px-4 py-8 text-center text-[0.9rem] text-[var(--theme-text-muted)]">
      <Users className="mx-auto mb-2 h-5 w-5 text-[var(--theme-accent-strong)]" aria-hidden="true" />
      {message}
    </div>
  );
}

function buildSalesReport(releases, selectedYear) {
  const releasedSales = releases
    .filter((release) => String(release.status || '') !== 'Pre-Released')
    .map(mapReleaseToSale)
    .filter((sale) => getYearFromDate(sale.releaseDate) === Number(selectedYear))
    .sort((firstSale, secondSale) => {
      const secondTimestamp = new Date(secondSale.releaseDate || 0).getTime();
      const firstTimestamp = new Date(firstSale.releaseDate || 0).getTime();

      return (
        secondTimestamp - firstTimestamp ||
        String(secondSale.releaseCode || '').localeCompare(String(firstSale.releaseCode || ''))
      );
    });
  const releasedKg = sumBy(releasedSales, (sale) => sale.releasedKg);
  const totalCost = sumBy(releasedSales, (sale) => sale.totalCost);
  const customerNames = new Set(releasedSales.map((sale) => sale.customerName).filter(Boolean));

  return {
    summary: {
      releasedKg,
      totalCost,
      releaseCount: releasedSales.length,
      customerCount: customerNames.size,
      averageKgPerRelease: releasedSales.length ? releasedKg / releasedSales.length : 0,
    },
    monthlySales: buildMonthlySales(releasedSales, selectedYear),
    recentSales: releasedSales,
  };
}

function filterRecentSales(items, filters, selectedYear) {
  const targetYear = Number(selectedYear);

  return items.filter((item) => {
    const itemDate = new Date(item.releaseDate || 0);

    if (Number.isNaN(itemDate.getTime()) || itemDate.getFullYear() !== targetYear) {
      return false;
    }

    if (filters.mode === 'month' && filters.month) {
      return `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}` === filters.month;
    }

    if (filters.mode === 'date' && filters.date) {
      return toDateInputValue(item.releaseDate) === filters.date;
    }

    if (filters.mode === 'custom') {
      const fromDate = parseLocalDateInput(filters.fromDate);
      const toDate = parseLocalDateInput(filters.toDate);
      const itemTime = itemDate.getTime();

      if (fromDate && itemTime < fromDate.getTime()) {
        return false;
      }

      if (toDate && itemTime > getEndOfLocalDay(toDate).getTime()) {
        return false;
      }

      return Boolean(fromDate || toDate);
    }

    return false;
  });
}

function getDefaultMonthFilter(selectedYear) {
  const today = new Date();
  const year = Number(selectedYear) || today.getFullYear();

  return `${year}-${String(today.getMonth() + 1).padStart(2, '0')}`;
}

function getDefaultDateFilter(selectedYear) {
  const today = new Date();
  const year = Number(selectedYear) || today.getFullYear();
  const month = today.getMonth();
  const day = Math.min(today.getDate(), new Date(year, month + 1, 0).getDate());

  return getLocalDateInputValue(new Date(year, month, day));
}

function getMonthDateRange(monthValue) {
  const [year, month] = String(monthValue || '').split('-').map(Number);
  const fallback = new Date();
  const targetYear = Number.isFinite(year) && year > 0 ? year : fallback.getFullYear();
  const targetMonth = Number.isFinite(month) && month >= 1 && month <= 12
    ? month - 1
    : fallback.getMonth();
  const startDate = new Date(targetYear, targetMonth, 1);
  const endDate = new Date(targetYear, targetMonth + 1, 0);

  return {
    from: getLocalDateInputValue(startDate),
    to: getLocalDateInputValue(endDate),
  };
}

function normalizeMonthFilter(value, selectedYear) {
  const [year = '', month = ''] = String(value || '').split('-');

  return Number(year) === Number(selectedYear) && month ? `${year}-${month}` : '';
}

function normalizeDateFilter(value, selectedYear) {
  return getYearFromDate(value) === Number(selectedYear) ? String(value || '') : '';
}

function parseLocalDateInput(value) {
  const [year, month, day] = String(value || '').split('-').map(Number);

  if (!year || !month || !day) {
    return null;
  }

  const parsedDate = new Date(year, month - 1, day);

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
}

function getEndOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function formatDateFilterLabel(value) {
  const parsedDate = parseLocalDateInput(value);

  if (!parsedDate) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function downloadRecentSalesPdf(items, year, filters) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });
  const pdfTheme = getPdfThemeColors();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 42;
  const contentWidth = pageWidth - margin * 2;
  const columns = {
    release: margin,
    batch: margin + 112,
    date: margin + 292,
    kg: margin + 440,
    cost: pageWidth - margin,
  };
  const filterLabel = formatRecentSalesFilterLabel(filters, year);
  const filename = `recent-releases-${year}${filterLabel ? `-${slugify(filterLabel)}` : ''}.pdf`;
  let y = 44;

  const drawTableHeader = () => {
    setPdfTextColor(doc, pdfTheme.text);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('Release No.', columns.release, y);
    doc.text('Batch / Customer', columns.batch, y);
    doc.text('Date', columns.date, y);
    doc.text('KG', columns.kg, y, { align: 'right' });
    doc.text('Cost', columns.cost, y, { align: 'right' });

    y += 16;
    setPdfDrawColor(doc, pdfTheme.borderSoft);
    doc.line(margin, y, pageWidth - margin, y);
    y += 18;
  };

  setPdfFillColor(doc, pdfTheme.accentStrong);
  doc.roundedRect(margin, y, contentWidth, 92, 18, 18, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('RECENT RELEASES', margin + 22, y + 28);
  doc.setFontSize(24);
  doc.text(String(year), margin + 22, y + 56);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(filterLabel || 'All releases', margin + 22, y + 76);

  y += 112;
  drawTableHeader();

  items.forEach((item) => {
    if (y > pageHeight - 58) {
      doc.addPage();
      y = 46;
      drawTableHeader();
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    setPdfTextColor(doc, pdfTheme.accentStrong);
    doc.text(String(item.releaseCode || '-'), columns.release, y);

    doc.setFont('helvetica', 'normal');
    setPdfTextColor(doc, pdfTheme.text);
    doc.text(String(item.soCode || '-').slice(0, 26), columns.batch, y);
    doc.text(String(item.customerName || '-').slice(0, 24), columns.batch, y + 13);
    doc.text(formatShortDate(item.releaseDate), columns.date, y);
    doc.text(formatKg(item.releasedKg), columns.kg, y, { align: 'right' });
    doc.text(formatPdfPeso(item.totalCost), columns.cost, y, { align: 'right' });

    y += 24;
    setPdfDrawColor(doc, pdfTheme.borderSoft);
    doc.line(margin, y, pageWidth - margin, y);
    y += 16;
  });

  doc.save(filename);
}

function formatRecentSalesFilterLabel(filters, year) {
  if (filters.mode === 'month' && filters.month) {
    const [targetYear, targetMonth] = String(filters.month).split('-').map(Number);

    if (targetYear && targetMonth) {
      return new Date(targetYear, targetMonth - 1, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });
    }
  }

  if (filters.mode === 'date' && filters.date) {
    return formatShortDate(filters.date);
  }

  if (filters.mode === 'custom') {
    const fromLabel = formatDateFilterLabel(filters.fromDate);
    const toLabel = formatDateFilterLabel(filters.toDate);

    if (fromLabel && toLabel) {
      return `${fromLabel} to ${toLabel}`;
    }

    if (fromLabel) {
      return `From ${fromLabel}`;
    }

    if (toLabel) {
      return `Until ${toLabel}`;
    }
  }

  return `Filtered ${year} releases`;
}

function slugify(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function formatPdfPeso(value) {
  return `PHP ${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
}

function getPdfThemeColors() {
  const readThemeColor = (propertyName, fallback) => {
    if (typeof window === 'undefined' || !window.getComputedStyle || !document?.documentElement) {
      return fallback;
    }

    return window.getComputedStyle(document.documentElement).getPropertyValue(propertyName).trim() || fallback;
  };

  return {
    accentStrong: parseCssColor(readThemeColor('--theme-accent-strong', '#467067')),
    borderSoft: parseCssColor(readThemeColor('--theme-border-soft', '#cddbd7')),
    text: parseCssColor(readThemeColor('--theme-text', '#364745')),
  };
}

function setPdfFillColor(doc, color) {
  doc.setFillColor(color.red, color.green, color.blue);
}

function setPdfTextColor(doc, color) {
  doc.setTextColor(color.red, color.green, color.blue);
}

function setPdfDrawColor(doc, color) {
  doc.setDrawColor(color.red, color.green, color.blue);
}

function parseCssColor(value) {
  const normalizedValue = String(value || '').trim();
  const hexMatch = normalizedValue.match(/^#([a-f\d]{3}|[a-f\d]{6})$/i);

  if (hexMatch) {
    const hexValue = hexMatch[1].length === 3
      ? hexMatch[1].split('').map((character) => `${character}${character}`).join('')
      : hexMatch[1];

    return {
      red: parseInt(hexValue.slice(0, 2), 16),
      green: parseInt(hexValue.slice(2, 4), 16),
      blue: parseInt(hexValue.slice(4, 6), 16),
    };
  }

  const rgbMatch = normalizedValue.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)/i);

  if (rgbMatch) {
    return {
      red: Number(rgbMatch[1]),
      green: Number(rgbMatch[2]),
      blue: Number(rgbMatch[3]),
    };
  }

  return { red: 54, green: 71, blue: 69 };
}

function mapReleaseToSale(release) {
  const releasedKg = Number(release.releasedKg || 0);
  const inventoryCost = sumBy(release.items || [], (item) => Number(item.subtotal || 0));
  const releaseExpense = Number(release.releasedSacks || 0) * Number(release.expensePerSack || 0);
  const totalCost = inventoryCost + releaseExpense;

  return {
    ...release,
    customerName: release.customerName || 'Walk-in Customer',
    releasedKg,
    inventoryCost,
    releaseExpense,
    totalCost,
  };
}

function buildMonthlySales(sales, selectedYear) {
  const year = Number(selectedYear);
  const today = new Date();
  const currentYear = today.getFullYear();
  const monthCount = year < currentYear
    ? 12
    : year === currentYear
      ? today.getMonth() + 1
      : 0;
  const salesByMonth = new Map(
    Array.from({ length: monthCount }, (_, monthIndex) => {
      const date = new Date(year, monthIndex, 1);
      const key = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

      return [
        key,
        {
          key,
          label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
          totalCost: 0,
          releasedKg: 0,
          releaseCount: 0,
        },
      ];
    })
  );

  sales.forEach((sale) => {
    const date = new Date(sale.releaseDate || 0);
    const key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const current = salesByMonth.get(key);

    if (!current) {
      return;
    }

    current.totalCost += sale.totalCost;
    current.releasedKg += sale.releasedKg;
    current.releaseCount += 1;
    salesByMonth.set(key, current);
  });

  return Array.from(salesByMonth.values()).sort((firstItem, secondItem) =>
    secondItem.key.localeCompare(firstItem.key)
  );
}

function getYearFromDate(value) {
  const date = new Date(value || 0);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.getFullYear();
}

function getCollapsedMonthlyItems(items, year) {
  if (!items.length) {
    return [];
  }

  const today = new Date();
  const selectedYear = Number(year);
  const currentYear = today.getFullYear();
  const targetMonth = selectedYear === currentYear ? today.getMonth() + 1 : 12;
  const targetKey = `${selectedYear}-${String(targetMonth).padStart(2, '0')}`;
  const targetItem = items.find((item) => item.key === targetKey);

  return [targetItem || items[0]].filter(Boolean);
}

function sumBy(items, selector) {
  return items.reduce((total, item) => total + Number(selector(item) || 0), 0);
}

function buildYAxisTicks(maxValue) {
  const normalizedMax = Math.max(Number(maxValue || 0), 1);
  const step = Math.ceil(normalizedMax / 4 / 1000) * 1000;

  return [0, step, step * 2, step * 3, step * 4];
}

function toChartPoint(index, value, totalItems, innerWidth, innerHeight, maxValue) {
  return {
    x: totalItems === 1 ? innerWidth / 2 : (index / (totalItems - 1)) * innerWidth,
    y: getChartY(value, innerHeight, maxValue),
  };
}

function getChartY(value, innerHeight, maxValue) {
  return innerHeight - (Number(value || 0) / Math.max(maxValue, 1)) * innerHeight;
}

function buildSmoothPath(points) {
  if (!points.length) {
    return '';
  }

  if (points.length === 1) {
    return `M ${points[0].x} ${points[0].y}`;
  }

  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const controlX = (current.x + next.x) / 2;

    path += ` C ${controlX} ${current.y}, ${controlX} ${next.y}, ${next.x} ${next.y}`;
  }

  return path;
}

function formatCompactNumber(value) {
  const numericValue = Number(value || 0);

  if (Math.abs(numericValue) >= 1000000) {
    return `${(numericValue / 1000000).toLocaleString('en-US', {
      maximumFractionDigits: 1,
    })}M`;
  }

  if (Math.abs(numericValue) >= 1000) {
    return `${(numericValue / 1000).toLocaleString('en-US', {
      maximumFractionDigits: 0,
    })}K`;
  }

  return numericValue.toLocaleString('en-US', {
    maximumFractionDigits: 0,
  });
}

function formatMonthLabel(value) {
  const [month = '', year = ''] = String(value || '').split(' ');

  return `${month.slice(0, 3)} ${year}`;
}

function formatKg(value) {
  return `${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} kg`;
}

function formatPesoWithDecimals(value) {
  return new Intl.NumberFormat('en-PH', {
    style: 'currency',
    currency: 'PHP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function getInventorySacksMark(item) {
  const sacksMark = String(item.sacksMark || '').trim();

  if (sacksMark) {
    return sacksMark;
  }

  const poCode = String(item.poCode || '').trim();
  const [markFromPoCode] = poCode.split('-');

  if (markFromPoCode) {
    return markFromPoCode;
  }

  const [markFromSupplier] = String(item.supplierName || '').split(' - ');
  return String(markFromSupplier || '').trim() || '-';
}

function getReleaseItemStatusLabel(item) {
  const status = String(item.poStatus || '').trim();

  if (status) {
    return status;
  }

  if (String(item.sourceType || '').toLowerCase() === 'merge' || item.mergeId) {
    return 'Merged Inventory';
  }

  return '-';
}

function formatSacksQuantity(value) {
  return `${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} sacks`;
}

function toDateInputValue(value) {
  const date = new Date(value || 0);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return getLocalDateInputValue(date);
}

function getLocalDateInputValue(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

export default SalesAnalyticsPage;
